/**
 * The server actions the auth screens call. The pages pass them in (the new
 * tree only imports `@/app/actions/*`), so the screens run exactly the
 * classic auth: same actions, same arguments, same results.
 */

import { isInAppBrowserAvailable, openInAppBrowser } from '@/lib/native/oauth-return'

/** What `login`, `signup`, `forgotPassword` and `updatePassword` return when they do not redirect. */
export type AuthActionResult =
  | { error?: string; code?: string; retryAfter?: number; success?: boolean }
  | undefined
  | void

export type FormAuthAction = (formData: FormData) => Promise<AuthActionResult>

/** `signInWithLinkedIn(inviteToken, flow, orgSlug, next, options)` from app/login/actions.ts. */
export type LinkedInAuthAction = (
  inviteToken?: string,
  flow?: 'login' | 'signup',
  orgSlug?: string,
  next?: string | null,
  options?: { native?: boolean }
) => Promise<{ error?: string; url?: string } | undefined | void>

/** A Next redirect thrown by a server action: the page is already leaving. */
export function isRedirectError(error: unknown): boolean {
  return !!(error as { digest?: string } | null)?.digest?.startsWith('NEXT_REDIRECT')
}

/**
 * Starts LinkedIn sign-in the way the classic form does since ALU-519: in the
 * native app with the in-app browser plugin the provider opens there (the
 * server returns the URL instead of redirecting) and the app finishes the
 * exchange; everywhere else the call is exactly the old one and the server
 * redirects. Returns `{ opened }` once the URL was handled here (`opened` is
 * false when it had to navigate this window instead), else the action's own
 * result.
 */
export async function startLinkedInSignIn(
  action: LinkedInAuthAction,
  ...args: [inviteToken?: string, flow?: 'login' | 'signup', orgSlug?: string, next?: string | null]
): Promise<Awaited<ReturnType<LinkedInAuthAction>> | { opened: boolean }> {
  const result = isInAppBrowserAvailable()
    ? await action(args[0], args[1], args[2], args[3], { native: true })
    : await action(...args)
  if (result && typeof result === 'object' && 'url' in result && result.url) {
    if (await openInAppBrowser(result.url)) return { opened: true }
    window.location.assign(result.url)
    return { opened: false }
  }
  return result
}
